import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Text,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { extractErrorMessage } from "../api/client";
import { EMP_BASE_PATH } from "../config";
import { appImages } from "../assets/appicons";

const GRAD = "linear(to-r, #68A2FF, #2168DA)";
const FIELD_BG = "#E7E7E7";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useEmployeeAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);

  const {
    register,
    handleSubmit,
    watch,
  } = useForm({ defaultValues: { phone: "", password: "" } });

  const phoneValue = watch("phone");
  const passwordValue = watch("password");
  const canSubmit = !!phoneValue?.trim() && !!passwordValue && !loading;

  const onSubmit = async ({ phone, password }) => {
    setLoading(true);
    try {
      await login(phone.trim(), password);
      toast.success("Đăng nhập thành công");
      navigate(EMP_BASE_PATH, { replace: true });
    } catch (err) {
      const status = err?.response?.data?.status || err?.response?.status;
      if (status === 404) toast.error("Tài khoản không tồn tại hoặc bị khóa!");
      else if (status === 401) toast.error("Mật khẩu không chính xác!");
      else toast.error(extractErrorMessage(err, "Đăng nhập thất bại"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      direction="column"
      minH="100vh"
      maxW={{ base: "480px", md: "100%" }}
      mx="auto"
      backgroundImage={`url(${appImages.image_background})`}
      backgroundSize="cover"
      backgroundPosition="center"
      bgColor="#909090"
      justify={{ base: "flex-start", md: "center" }}
      align={{ base: "stretch", md: "center" }}
    >
      {/* Logo */}
      <Flex
        flex={{ base: "1", md: "0" }}
        align="flex-start"
        justify="center"
        pt={{ base: "120px", md: "0" }}
        pb={{ base: "0", md: "24px" }}
      >
        <img src={appImages.white_logo} alt="MSH GROUP" style={{ width: 180, height: "auto", objectFit: "contain" }} />
      </Flex>

      {/* Card đăng nhập: đáy màn ở mobile, canh giữa ở web */}
      <Box
        mx="16px"
        mb={{ base: "12px", md: "0" }}
        w={{ base: "auto", md: "420px" }}
        bg="white"
        borderRadius="16px"
        p="20px"
        boxShadow={{ base: "none", md: "0 10px 30px rgba(0,0,0,0.18)" }}
      >
        <Text fontSize="22px" fontWeight={800} color="#08142D">
          Đăng nhập
        </Text>

        <Box h="20px" />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Input
            placeholder="Mã nhân viên"
            h="48px"
            fontSize="14px"
            bg={FIELD_BG}
            border="none"
            borderRadius="8px"
            _placeholder={{ color: "#9D9D9D" }}
            _focus={{ boxShadow: "none", bg: FIELD_BG }}
            {...register("phone", { required: true })}
          />

          <Box h="12px" />

          <InputGroup>
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Mật khẩu"
              h="48px"
              fontSize="14px"
              bg={FIELD_BG}
              border="none"
              borderRadius="8px"
              _placeholder={{ color: "#9D9D9D" }}
              _focus={{ boxShadow: "none", bg: FIELD_BG }}
              {...register("password", { required: true })}
            />
            <InputRightElement h="48px" w="48px">
              <Box as="button" type="button" onClick={() => setShowPassword((s) => !s)} color="#6B7280">
                {showPassword ? <ViewIcon /> : <ViewOffIcon />}
              </Box>
            </InputRightElement>
          </InputGroup>

          <Box h="12px" />

          {/* Lưu tài khoản + Quên mật khẩu */}
          <Flex align="center" justify="space-between">
            <Flex as="button" type="button" align="center" gap="8px" onClick={() => setRemember((v) => !v)}>
              <Flex
                w="18px"
                h="18px"
                borderRadius="4px"
                border="2px solid"
                borderColor={remember ? "#2168DA" : "#C8CCD6"}
                bg={remember ? "#2168DA" : "white"}
                align="center"
                justify="center"
              >
                {remember && (
                  <Box as="span" color="white" fontSize="12px" lineHeight="1">
                    ✓
                  </Box>
                )}
              </Flex>
              <Text fontSize="14px" color="#08142D">Lưu tài khoản</Text>
            </Flex>

            <Text
              as="button"
              type="button"
              fontSize="14px"
              color="#4081EB"
              onClick={() => toast("Vui lòng liên hệ quản trị viên để đặt lại mật khẩu")}
            >
              Quên mật khẩu?
            </Text>
          </Flex>

          <Box h="30px" />

          <Button
            type="submit"
            isLoading={loading}
            isDisabled={!canSubmit}
            w="100%"
            h="50px"
            bgImage={GRAD}
            color="white"
            borderRadius="10px"
            fontWeight={700}
            _hover={{ bgImage: GRAD, opacity: 0.92 }}
            _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
          >
            Đăng nhập
          </Button>
        </form>
      </Box>

      <Flex direction="column" align="center" gap="6px" pb="18px">
        <Text
          as="button"
          type="button"
          onClick={() => navigate("/login")}
          fontSize="12px"
          fontWeight={600}
          color="rgba(255,255,255,0.9)"
        >
          Đăng nhập trang Quản trị →
        </Text>
        <Text fontSize="12px" fontWeight={700} color="rgba(255,255,255,0.78)">
          © MSH
        </Text>
      </Flex>
    </Flex>
  );
};

export default Login;
